// The Gitee half of a connected webhook source (plan 101 slice 10b): the repo a `gitee`
// source observes, the hint its ingest mapper hands the worker, and the narrowing of the
// two-arm `forge_project` union (D11).
//
// READ-ONLY, gitee.com only (D9: no host input, no Gitee Enterprise). A `gitee` source's
// deliveries file no raw task: the mapper enqueues a HINT naming the pull request, and the
// worker re-reads it through the Gitee v5 client (`@developerz/gitee`, whose fixed base
// is the one place the API host is spelled) with the source's own personal access token,
// and writes one `forge.pr.observed` audit row. Gitee signs only a timestamp, never the
// body, so the payload is never trusted for state: it only says which PR to look at.
//
// A LEAF: zod, `value-objects/ids` and type-only imports. `audit/payload` imports
// {@link GiteeRepoPathSchema} from here, and entering the audit graph through anything
// heavier is the TDZ cycle that file's header names.

import { z } from 'zod';
import { AccountIdSchema, WebhookSourceIdSchema } from '../value-objects/ids';
import type { ForgeProject } from './gitlab-forge';

/**
 * One Gitee path segment (an owner or a repo `path`): letters, digits, `_`, `-`, `.`,
 * never starting with `-` or `.`, so `.` / `..` (which rewrite a URL path) cannot pass.
 * Gitee's own rule is stricter in places; this is the fence a READ path needs.
 */
const GITEE_PATH_SEGMENT = /^[A-Za-z0-9_][A-Za-z0-9_.-]*$/;

/** One owner or repo segment, as Gitee spells it in `/repos/{owner}/{repo}`. */
export const GiteePathSegmentSchema = z.string().min(1).max(191).regex(GITEE_PATH_SEGMENT);

/**
 * A repo's `owner/repo` (`full_name`): EXACTLY two segments. Unlike GitLab, Gitee has no
 * nested namespaces in a repo path, and `owner/repo` is the read key (v5 has no
 * id-addressed repo route), so a third segment would read a different resource.
 */
export const GiteeRepoPathSchema = z
  .string()
  .min(3)
  .max(383)
  .refine((p) => {
    const segments = p.split('/');
    return segments.length === 2 && segments.every((s) => GITEE_PATH_SEGMENT.test(s));
  }, 'expected a Gitee owner/repo such as "openharmony/docs"');

/**
 * `webhook_sources.forge_project` on a `gitee` row: the repo the source observes. Filled
 * by the connect leg from ONE read of the repo with the source's own token (which proves
 * the token and resolves `repoId`), never from a caller's say-so.
 *
 * Structurally disjoint from the GitLab arm (`ForgeProjectSchema`: `instance`,
 * `projectId`, `pathWithNamespace`), and both are strict, so a stored object parses as at
 * most one arm. The ROW's `kind` says which arm it must be (`WebhookSourceSchema`).
 *
 * A stored shape: a field added later must be optional or arrive with a backfill.
 */
export const GiteeRepoSchema = z.strictObject({
  /** The owner path (user, org or enterprise), as read at connect. */
  owner: GiteePathSegmentSchema,
  /** The repo path, as read at connect. With `owner`, the read key. */
  repo: GiteePathSegmentSchema,
  /**
   * Gitee's numeric repo id. A rename leaves `owner/repo` stale (every read then 404s
   * `gitee_repo_not_found`); the id is the cross-check against the delivery's `project.id`
   * and the PR read's `base.repo.id`.
   */
  repoId: z.number().int().positive(),
});
export type GiteeRepo = z.infer<typeof GiteeRepoSchema>;

/** The stored `forge_project` value of either forge kind. */
export type ForgeProjectArm = ForgeProject | GiteeRepo;

/** Which forge kind a parsed `forge_project` arm belongs to, read off its shape. */
export function forgeProjectKind(project: ForgeProjectArm): 'gitlab' | 'gitee' {
  return 'instance' in project ? 'gitlab' : 'gitee';
}

/** The fields the narrowing reads: a parsed `WebhookSource` or any row-shaped subset. */
type ForgeArmHolder = { readonly kind: string; readonly forgeProject: ForgeProjectArm | null };

/**
 * The GitLab project of a `gitlab` source, or null on any other kind. Every reader of a
 * GitLab `forge_project` goes through this rather than reading the union field: the
 * schema's iff makes the arm match the kind on a parsed row, and this is the narrowing.
 */
export function gitlabProjectOf(source: ForgeArmHolder): ForgeProject | null {
  const project = source.forgeProject;
  if (source.kind !== 'gitlab' || project === null || !('instance' in project)) return null;
  return project;
}

/** The Gitee repo of a `gitee` source, or null on any other kind ({@link gitlabProjectOf}'s twin). */
export function giteeRepoOf(source: ForgeArmHolder): GiteeRepo | null {
  const project = source.forgeProject;
  if (source.kind !== 'gitee' || project === null || 'instance' in project) return null;
  return project;
}

/**
 * The job the ingest mapper enqueues for an accepted Gitee delivery (a PR event, or a
 * comment on a PR) and the worker consumes, on `GITEE_PR_HINT_QUEUE` (`@developerz/queue`).
 * A HINT: it names which pull request to re-read, never what state it is in.
 *
 * `sourceId` / `accountId` / `repoId` are copied off the ROW at ingest, never from the
 * payload (nothing in a Gitee body is signed). `number` is the PR's per-repo number
 * (`pull_request.number`), the one the REST paths take.
 */
export const GiteePrHintSchema = z.strictObject({
  sourceId: WebhookSourceIdSchema,
  accountId: AccountIdSchema,
  repoId: z.number().int().positive(),
  number: z.number().int().positive(),
  /**
   * The authenticated `X-Gitee-Timestamp` (epoch ms), verbatim as a digit string: the
   * delivery's replay key (D5) and `inbound_events.delivery_id`, both strings.
   */
  deliveryId: z.string().regex(/^\d{1,16}$/, 'expected the X-Gitee-Timestamp ms digits'),
});
export type GiteePrHint = z.infer<typeof GiteePrHintSchema>;
